import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CheckoutService } from 'src/app/services/checkout/checkout.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { PaymentConfigService } from 'src/app/services/payment-config/payment-config.service';
import { UserService } from 'src/app/services/user/user.service';
import { ProductType, User } from 'src/app/interfaces/user';
import { TokPaymentPageConfig } from 'src/app/interfaces/pageConfig';
import { RegistrationFlowStep } from 'src/app/constants/registration-flow.constants';
import {
  MULTI_PLAN_TOK_STRIPE_PLANS,
  getMultiPlanTokRequest,
} from 'src/app/constants/tok-payment.constants';

@Component({
  selector: 'app-tok-payment-v2',
  templateUrl: './tok-payment-v2.component.html',
  styleUrl: './tok-payment-v2.component.scss',
})
export class TokPaymentV2Component implements OnInit {
  public RegistrationFlowStep = RegistrationFlowStep;

  public page: TokPaymentPageConfig;
  public pageTitle: string;
  public description: string;
  public inheritedLabel: string;
  public multiPlanIntro: string;

  public petName = '';
  public plan2Name: string;
  public plan2YearlyPrice: string;

  public plan1Available = true;

  // Second card, shown only for the Rescue / BasePlusBreederSale flows.
  public isMultiPlanTok = false;
  public plan1Name: string;
  public plan1Title: string;
  public plan1YearlyPrice: string;
  public plan1Benefits: string[];
  public plan1StripePlanId: string;
  public plan2StripePlanId: string;

  public readonly standardTokStripePlanId = environment.stripePlans.identiAlertTokBreeder;

  private user: User;

  constructor(
    private checkoutService: CheckoutService,
    private route: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private featureFlags: FeatureFlagsService,
    private paymentConfigService: PaymentConfigService,
    private userService: UserService,
  ) {
    this.user = this.route.snapshot.data.user as User;

    this.page = this.configService.getPage('tokPayment');
    this.plan1Name = this.configService.getTextFromKey('1-plan-name');
    this.plan1Title = this.configService.getTextFromKey('1-plan-title');
    this.plan1YearlyPrice = this.configService.getTextFromKey('1-plan-yearly-price');
    this.plan1Benefits = this.configService.getListContentFromKey('1-plan-benefits');
    this.plan2Name = this.configService.getTextFromKey('2-plan-name');

    this.plan1YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.BasicPlus) ??
      this.plan1YearlyPrice;
    this.plan2YearlyPrice =
      this.paymentConfigService.getYearlyDisplayPrice(ProductType.Premium) ??
      this.configService.getTextFromKey('2-plan-yearly-price');

    this.plan1Available = this.paymentConfigService.isProductAvailable(ProductType.BasicPlus);

    this.redirectToDashboard();
  }

  ngOnInit(): void {
    // The URL has no pet name when the user is redirected here, e.g. after logging in again.
    this.petName =
      this.route.snapshot.queryParamMap.get('petName') ??
      this.userService.filterTokPets(this.user.pets)[0]?.name ??
      '';

    this.pageTitle = this.withPlaceholders(this.page?.pageTitle);
    this.description = this.withPlaceholders(this.page?.description);
    this.inheritedLabel = this.withPlaceholders(this.page?.inheritedLabel);
    this.multiPlanIntro = this.withPlaceholders(this.page?.multiPlanIntro);
    this.plan1Title = this.withPlaceholders(this.plan1Title);

    const multiPlanTokRequest = getMultiPlanTokRequest(this.user);
    this.isMultiPlanTok =
      !!multiPlanTokRequest && this.featureFlags.isEnabled('isTokPlanSelectionAvailable');

    if (this.isMultiPlanTok) {
      const multiPlanTokStripePlans = MULTI_PLAN_TOK_STRIPE_PLANS[multiPlanTokRequest.type];
      this.plan1StripePlanId = multiPlanTokStripePlans.plan1;
      this.plan2StripePlanId = multiPlanTokStripePlans.plan2;
    }
  }

  /** The TOK card buys the standard plan, or this flow's own plan 2 when there is a choice. */
  public get tokStripePlanId(): string {
    return this.isMultiPlanTok ? this.plan2StripePlanId : this.standardTokStripePlanId;
  }

  public buyTok(planId: string = this.standardTokStripePlanId): void {
    this.checkoutService.checkout([planId]).subscribe();
  }

  private withPlaceholders(text: string): string {
    return text
      ?.replace('{{petName}}', this.petName)
      .replace('{{planName}}', this.plan2Name);
  }

  private redirectToDashboard(): void {
    !this.user.tokRequests.length && this.router.navigate(['/dashboard']);
  }
}
